import { useMemo } from 'react';
import { getName } from '@home/utils/common';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import { commonDownloadValueFormatter } from '@/pages/dashboard/utils/utils';

export const useTableData = ({
  data,
  isEN,
  tableDateMap,
  dateNameMap,
  formatRule,
  downloadFormatRule,
  lastDate,
  competitorList,
  currentTab
}: any) => {
  // 表格数据处理
  const { tableData, downloadData } = useMemo(() => {
    const rawList = data?.list as {
      date: string;
      label: string;
      metrics: Record<string, { value: string; }>;
    }[] || [];
  
    const filteredList = rawList
      // 跳过第一项
      .slice(1)
      // 过滤掉 label 为空的，除了 date 是 lastDate 的情况
      .filter(item => item.label || item.date === lastDate);

    // 提取公共的数据处理逻辑
    const processDataItem = ({ date, label, metrics }: any, formatter: (value: string) => any) => {
      const formattedDate = getName(
        isEN,
        tableDateMap?.[date] || date,
        dateNameMap?.[tableDateMap?.[date] || date]
      );
      
      const formattedMetrics = Object.fromEntries(
        Object.entries(metrics).map(([key, val]: [string, any]) => [
          key,
          formatter(val.value),
        ])
      );

      // 如果是 lastDate 且没有 label，则将 upper/lower_forecast 替换为 "-"
      if (date === lastDate && !label) {
        formattedMetrics.lower_forecast = "-";
        formattedMetrics.upper_forecast = "-";
      }

      return {
        date: formattedDate,
        label,
        ...formattedMetrics,
      };
    };

    const tableData = filteredList.map(item => 
      processDataItem(item, (value) => dataFormat({ value, formatRule }))
    );
    
    const downloadData = filteredList.map(item => 
      processDataItem(item, (value) => commonDownloadValueFormatter(downloadFormatRule, value))
    );
  
    return { tableData, downloadData };
  }, [data, isEN, tableDateMap, dateNameMap, formatRule, downloadFormatRule, lastDate]);

  // 表头处理
  const columns = useMemo(() => {
    const extColumns = currentTab?.componentMetrics?.map((item: any) => ({
      header: getName(isEN, item?.enName, item?.cnName),
      key: item?.metricKey,
    })) || [];
  
    const competitorColumns =
      (competitorList as { combined_id: string; game_name: string; }[])
        ?.filter(({ combined_id }) =>
          tableData?.some(row => Object.keys(row)?.includes(combined_id))
        )
        ?.map(({ combined_id, game_name }) => ({
          header: game_name,
          key: combined_id,
        })) || [];
  
    return [
      {
        header: getName(isEN, "Period", "时段"),
        key: "date",
      },
      ...extColumns,
      ...competitorColumns,
    ];
  }, [isEN, currentTab?.componentMetrics, competitorList, tableData]);

  return { tableData, downloadData, columns };
};
